import { getSimpleBoolSelectItems, IFieldInfoProperties, IGetValueArgs, not } from "@components/smart/FieldInfo";
import { ISmartFieldChange } from "@components/smart/smartField/SmartField";
import BindingContext, { createPath, IEntity } from "@odata/BindingContext";
import { getNestedValue, setDirtyFlag } from "@odata/Data.utils";
import {
    BankAccountEntity,
    EntitySetName,
    IPrRetirementSavingsCompanyEntity,
    PrEmploymentEntity,
    PrEmploymentRetirementSavingEntity,
    PrEmploymentRetirementSavingTemporalCurrentEntity,
    PrEmploymentRetirementSavingTemporalEntity,
    PrPayslipEmploymentRetirementSavingEntity,
    PrRetirementSavingsCompanyEntity,
    PrRetirementSavingsComputationTypeEntity,
    PrRetirementSavingsProductEntity,
    PrRetirementSavingsTypeEntity
} from "@odata/GeneratedEntityTypes";
import { PrRetirementSavingsComputationTypeCode } from "@odata/GeneratedEnums";
import { setTemporalCurrentValue } from "@odata/TemporalUtils";
import { TFieldsDefinition } from "@pages/PageUtils";
import { getPayrollCurrencyFieldDef } from "@pages/payroll/payroll.utils";
import { productPath } from "@pages/payroll/payslip/Payslip.utils";
import { formatBankAccountWithoutType } from "@utils/BankUtils";
import i18next from "i18next";

import { EMPTY_DASH } from "../../../constants";
import { BasicInputSizes, FastEntryInputSizes, FieldType, ValidatorType } from "../../../enums";
import { TRecordAny } from "../../../global.types";
import { FormStorage } from "../../../views/formView/FormStorage";

export const getRetirementSavingsBasicFields = (prefix?: string): TFieldsDefinition => {
    return {
        [createPath(prefix, PrEmploymentEntity.RetirementSavings, PrEmploymentRetirementSavingEntity.RetirementSavingsProduct, PrRetirementSavingsProductEntity.Type)]: {
            type: FieldType.ComboBox,
            width: BasicInputSizes.L,
            fieldSettings: {
                displayName: PrRetirementSavingsTypeEntity.Name
            }
        },
        [createPath(prefix, PrEmploymentEntity.RetirementSavings, productPath)]: {
            type: FieldType.ComboBox,
            isRequired: true,
            width: BasicInputSizes.L,
            label: i18next.t("RetirementSavingsCompany:Company"),
            filter: {
                select: (args: IGetValueArgs, otherArgs?: TRecordAny) => {
                    const bc = args.bindingContext.getParent().navigate(PrPayslipEmploymentRetirementSavingEntity.RetirementSavingsProduct).navigate(PrRetirementSavingsProductEntity.Type);
                    const type = args.storage.getValue(bc)?.Code;

                    return `Products/any(p: p/Type/Code eq '${type}')`;
                }
            },
            fieldSettings: {
                entitySet: EntitySetName.PrRetirementSavingsCompanies,
                additionalProperties: [
                    { id: createPath(PrRetirementSavingsCompanyEntity.Products, PrRetirementSavingsProductEntity.Type) },
                    { id: createPath(PrRetirementSavingsCompanyEntity.Products, PrRetirementSavingsProductEntity.Id) },
                ],
                displayName: PrRetirementSavingsCompanyEntity.Name,
                shouldDisplayAdditionalColumns: true,
                preloadItems: true,
            },
            columns: [
                { id: PrRetirementSavingsCompanyEntity.Name },
                {
                    id: createPath(PrRetirementSavingsCompanyEntity.Products, PrRetirementSavingsProductEntity.BankAccount),
                    formatter: (val, opts) => {
                        const { storage, fieldBindingContext, item } = opts;
                        const bc = fieldBindingContext.getParent().navigate(PrEmploymentRetirementSavingEntity.RetirementSavingsProduct).navigate(PrRetirementSavingsProductEntity.Type);
                        const currentType = storage.getValue(bc, { useDirectValue: false });
                        const product = (item as IPrRetirementSavingsCompanyEntity).Products?.find(p =>
                            p.TypeCode === currentType);

                        return product?.BankAccount?.Id ? formatBankAccountWithoutType(product?.BankAccount) : EMPTY_DASH;
                    },
                    additionalProperties: [
                        { id: BankAccountEntity.AccountNumber },
                        { id: BankAccountEntity.BankCode },
                        { id: BankAccountEntity.IBAN },
                        { id: BankAccountEntity.AbaNumber },
                    ]
                },
            ]
        }
    };
};

const isMandatoryContributionRetirementSaving = ({ storage, bindingContext }: IGetValueArgs): boolean => {
    const compTypeCode = storage.getValue(bindingContext.getParent().navigate(PrEmploymentRetirementSavingTemporalCurrentEntity.ComputationTypeCode)) as PrRetirementSavingsComputationTypeCode;
    return compTypeCode === PrRetirementSavingsComputationTypeCode.MandatoryContribution;
};

export const setupRetirementSavingsCompanyId = (entity: IEntity) => {
    for (const rs of entity.RetirementSavings || []) {
        const company = rs.RetirementSavingsProduct?.PrRetirementSavingsCompany;
        rs[productPath] = company?.Id;
    }
};

export const handleRetirementSavingsLineItemsChange = (e: ISmartFieldChange, storage: FormStorage) => {
    const collectionBc = e.bindingContext.getParentCollection();
    const collectionPath = collectionBc.getPath(true);
    if (collectionPath === PrEmploymentEntity.RetirementSavings) {
        const path = e.bindingContext.getPath();
        if (path === PrRetirementSavingsProductEntity.Type) {
            const companyBc = e.bindingContext.getParent().getParent().navigate(productPath);
            const companyInfo = storage.getInfo(companyBc);
            companyInfo.fieldSettings.items = null;
            storage.clearValue(companyBc);
            // we set dirty for formatter reasons of product selector
            setDirtyFlag(storage, companyBc);
        }

        if (path === PrEmploymentRetirementSavingTemporalCurrentEntity.ComputationType) {
            if (e.value !== PrRetirementSavingsComputationTypeCode.MandatoryContribution) {
                const parentBc = e.bindingContext.getParent();
                let isIncludedBc: BindingContext;
                if (parentBc.isValidNavigation(PrEmploymentRetirementSavingTemporalCurrentEntity.IsIncludedAutomatically)) {
                    isIncludedBc = parentBc.navigate(PrEmploymentRetirementSavingTemporalCurrentEntity.IsIncludedAutomatically);
                } else {
                    isIncludedBc = parentBc.navigate(PrPayslipEmploymentRetirementSavingEntity.IsIncluded);
                }
                storage.clearValue(isIncludedBc);
                if (storage.data.bindingContext.isValidNavigation("TemporalPropertyBag")) {
                    setTemporalCurrentValue(storage, isIncludedBc, null);
                }
            }
            storage.refreshGroup(e.bindingContext);
        }

        if (path === productPath) {
            const productBc = e.bindingContext.getParent().navigate(PrPayslipEmploymentRetirementSavingEntity.RetirementSavingsProduct).navigate(PrRetirementSavingsProductEntity.Id);
            storage.setValue(productBc, e.value);
        }
    }
};

export const getRetirementSavingsComputationTypeDef = (isIncludedPath: string = PrEmploymentRetirementSavingTemporalEntity.IsIncludedAutomatically) => {
    return {
        type: FieldType.ComboBox,
        width: BasicInputSizes.L,
        label: i18next.t("RetirementSavingsCompany:Form.ComputationType"),
        defaultValue: PrRetirementSavingsComputationTypeCode.FixedAmount,
        fieldSettings: {
            preloadItems: true,
            displayName: PrRetirementSavingsComputationTypeEntity.Name,
            entitySet: EntitySetName.PrRetirementSavingsComputationTypes,
        },
        affectedFields: [
            { id: PrEmploymentRetirementSavingTemporalEntity.Amount, navigateFromParent: true },
            { id: isIncludedPath, navigateFromParent: true }
        ]
    };
};

export const getRetirementSavingsIsIncludedAutomaticallyDef = () => {
    return {
        type: FieldType.ComboBox,
        fieldSettings: {
            items: getSimpleBoolSelectItems()
        },
        isRequired: isMandatoryContributionRetirementSaving,
        isDisabled: not(isMandatoryContributionRetirementSaving)
    };
};

export const areAllRetirementsOfSameType = (path = "ComputationTypeCode", args: IGetValueArgs): boolean => {
    const { storage, bindingContext } = args;
    const retirements = storage.getValue(bindingContext) ?? [];
    if (retirements.length < 2) {
        return true;
    }
    const _getProp = (e: IEntity) => getNestedValue(path, e);
    const refType = _getProp(retirements[0]);
    return !retirements.slice(1).find((sc: IEntity) => _getProp(sc) !== refType);
};


export const getRetirementSavingsSecondaryFields = (): Record<string, IFieldInfoProperties> => {
    return {
        [PrEmploymentRetirementSavingTemporalCurrentEntity.Percentage]: {
            isVisible: (args: IGetValueArgs) => {
                const computationType = args.storage.getValue(args.bindingContext.getParent().navigate(PrEmploymentRetirementSavingTemporalCurrentEntity.ComputationType))?.Code;
                const isFixed = computationType === PrRetirementSavingsComputationTypeCode.FixedAmount;
                return !isFixed;
            },
            isRequired: true,
            type: FieldType.NumberInput,
            width: FastEntryInputSizes.S,
            fieldSettings: {
                showSteppers: true,
                unit: "%"
            },
            validator: {
                type: ValidatorType.Number,
                settings: {
                    min: 0,
                    max: 100
                },

            }
        },
        [PrEmploymentRetirementSavingTemporalCurrentEntity.Amount]: {
            ...getPayrollCurrencyFieldDef(),
            isRequired: true,
            isVisible: (args: IGetValueArgs) => {
                const computationType = args.storage.getValue(args.bindingContext.getParent().navigate(PrEmploymentRetirementSavingTemporalCurrentEntity.ComputationType))?.Code;
                const isFixed = computationType === PrRetirementSavingsComputationTypeCode.FixedAmount;
                return isFixed;
            },
            type: FieldType.NumberInput,
            width: FastEntryInputSizes.S,
            fieldSettings: {
                ...getPayrollCurrencyFieldDef().fieldSettings
            },
            validator: {
                type: ValidatorType.Number,
                settings: { min: 0 }
            }
        },
        [PrEmploymentRetirementSavingTemporalCurrentEntity.SymbolVariable]: {},
        [PrEmploymentRetirementSavingTemporalCurrentEntity.SymbolConstant]: {},
        [PrEmploymentRetirementSavingTemporalCurrentEntity.SymbolSpecific]: {},
        [PrEmploymentRetirementSavingTemporalCurrentEntity.RemittanceInformation]: {
            width: BasicInputSizes.L,
        }
    };
};